import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  CssBaseline,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Typography,
  TextField,
  CircularProgress,
} from '@mui/material';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { RESULTS_EXPLORER_API_URL } from '../config';
import { useStoreData } from '../useStoreData';
import '../App.css';

/* ---------- THEME ---------- */
const customTheme = createTheme({
  palette: {
    primary: { main: '#ff1744', contrastText: '#ffffff' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  typography: {
    h5: { fontWeight: 600, fontSize: '0.875rem' },
    body1: { fontSize: '0.875rem' },
  },
  components: {
    MuiInputLabel: {
      styleOverrides: {
        root: { color: '#ffffff', fontSize: '0.875rem' },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          backgroundColor: '#333333',
          fontSize: '0.875rem',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
        },
      },
    },
    MuiMenu: {
      styleOverrides: { paper: { backgroundColor: '#333333', color: '#ffffff' } },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          fontSize: '0.875rem',
          '&.Mui-selected': { backgroundColor: '#444444' },
          '&.Mui-selected:hover': { backgroundColor: '#555555' },
          '&:hover': { backgroundColor: '#444444' },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: { color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } },
      },
    },
  },
});

/* ---------- HELPERS ---------- */
const roiColor = (val) => {
  if (val == null) return '#ffffff';
  const pct = val * 100;
  if (pct <= -10) return 'rgb(255,0,0)';
  if (pct < 0) {
    const t = (pct + 10) / 10;
    return `rgb(255,${Math.round(255 * t)},${Math.round(255 * t)})`;
  }
  if (pct <= 10) {
    const t = pct / 10;
    return `rgb(${Math.round(255 * (1 - t))},255,${Math.round(255 * (1 - t))})`;
  }
  return 'rgb(0,255,0)';
};

const RoiCell = ({ value }) => {
  if (value == null) return '-';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <Typography sx={{ color: roiColor(value), fontSize: '0.875rem', fontWeight: 500 }}>
        {(value * 100).toFixed(2)}%
      </Typography>
    </Box>
  );
};

const PctCell = ({ value }) => {
  if (value == null) return '-';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <Typography sx={{ fontSize: '0.875rem' }}>
        {(value * 100).toFixed(1)}%
      </Typography>
    </Box>
  );
};

const NumCell = ({ value }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
    <Typography sx={{ fontSize: '0.875rem' }}>
      {value != null ? value.toLocaleString() : 0}
    </Typography>
  </Box>
);

/* ---------- CONSTANTS ---------- */
const GROUP_BY_OPTIONS = [
  { value: 'book', label: 'Book' },
  { value: 'edge', label: 'Edge Bucket' },
  { value: 'league', label: 'League' },
  { value: 'market', label: 'Market' },
  { value: 'month', label: 'Month' },
  { value: 'snapshot', label: 'Time to Close' },
];

const GROUP_LABEL_HEADERS = {
  book: 'Book',
  edge: 'Edge Bucket',
  league: 'League',
  market: 'Market',
  month: 'Month',
  snapshot: 'Time to Close',
};

// Pickem book name normalizer — mirrors dbo.OddsSnapshots.sportsbook_display_name
// computed column on the backend, and PICKEM_PLATFORMS prefixes in Pickem.js.
// Collapses platform variants (e.g. "Underdog Fantasy (2 Pick)", "(Multipliers)")
// into a single platform label so the books dropdown surfaces "Underdog" once
// and the filter sends a label that matches the normalized server-side column.
// Non-pickem books pass through unchanged.
const normalizePickemBookName = (name) => {
  if (!name) return name;
  if (name.startsWith('Underdog')) return 'Underdog';
  if (name.startsWith('PrizePicks')) return 'PrizePicks';
  if (name.startsWith('DraftKings (Pick')) return "DraftKings Pick'em";
  if (name.startsWith('Sleeper')) return 'Sleeper';
  return name;
};

/* ---------- MAIN COMPONENT ---------- */
export default function Results() {
  /* ── Filter state ── */
  const [groupBy, setGroupBy] = useState('book');
  const [league, setLeague] = useState('All');
  const [bookFilter, setBookFilter] = useState([]);
  const [minEdge, setMinEdge] = useState(2);
  const [fromDate, setFromDate] = useState(() => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
});
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));

  /* ── Data state ── */
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /* ── Filter dropdowns from shared cache (same pattern as EVTool) ── */
  const sportsbooksCache = useStoreData('sportsbooks');
  const leaguesCache = useStoreData('leagues');

  const availableBooks = useMemo(() => {
    // Temporarily excluding pickem books while pickem-aware indexing is sorted out.
    // Pickem entries in the cache are flagged with sb.pickem === true.
    return (sportsbooksCache || [])
      .filter(sb => !sb.pickem)
      .map(sb => sb.name)
      .filter(Boolean)
      .sort();
  }, [sportsbooksCache]);

  const availableLeagues = useMemo(() => {
    return (leaguesCache || [])
      .map(l => l.id)
      .filter(Boolean)
      .sort();
  }, [leaguesCache]);

  // Map league id -> display name from the cache (same source EVTool uses)
  const leagueLabel = useCallback((id) => {
    const match = (leaguesCache || []).find(l => l.id === id);
    return (match && (match.name || match.id)) || id;
  }, [leaguesCache]);

  // On first load of cached books, default the bookFilter to all of them
  const bookFilterInitialized = useRef(false);
  useEffect(() => {
    if (!bookFilterInitialized.current && availableBooks.length > 0) {
      setBookFilter(availableBooks);
      bookFilterInitialized.current = true;
    }
  }, [availableBooks]);

  // Debounce ref
  const debounceRef = useRef(null);

  /* ── Fetch data ── */
  const abortRef = useRef(null);
  const fetchData = useCallback(async () => {
    // Cancel any in-flight request from a previous filter change
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('groupBy', groupBy);
      if (league !== 'All') params.set('league', league);
      params.set('minEdge', minEdge.toString());
      if (fromDate) params.set('from', fromDate);
      if (toDate) params.set('to', toDate);
      if (bookFilter.length > 0) params.set('books', bookFilter.join(','));

      const resp = await fetch(`${RESULTS_EXPLORER_API_URL}?${params.toString()}`, {
        signal: controller.signal,
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();

      // Guard against stale responses if a newer request was started
      if (controller.signal.aborted) return;

      setSummary(data.summary);
      setRows((data.rows || []).map((r, i) => ({ ...r, id: i })));
    } catch (err) {
      // Ignore aborts — they're expected when filters change quickly
      if (err.name === 'AbortError') return;
      console.error('[RESULTS-EXPLORER] Fetch error:', err);
      setError(err.message);
    } finally {
      // Only clear loading if this is still the active request
      if (abortRef.current === controller) {
        setLoading(false);
      }
    }
  }, [groupBy, league, bookFilter, minEdge, fromDate, toDate]);

  // Debounced fetch on filter change
  // Wait until the book filter has been initialized from the cache before
  // firing the first request — otherwise we make a wasted call with no books
  // selected, then immediately abort it once the cache populates.
  useEffect(() => {
    if (!bookFilterInitialized.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchData, 1500);
    return () => clearTimeout(debounceRef.current);
  }, [fetchData]);

  /* ── Book filter handlers ── */
  const handleBookChange = (e) => {
    const val = e.target.value;
    if (val[val.length - 1] === 'all') {
      setBookFilter(bookFilter.length === availableBooks.length ? [] : [...availableBooks]);
    } else {
      setBookFilter(val);
    }
  };

  const renderBookValue = (selected) => {
    if (!selected.length) return 'All';
    if (selected.length === availableBooks.length) return 'All';
    return selected.length > 1 ? `${selected.length} selected` : selected[0];
  };

  /* ── Columns (update first col header based on groupBy) ── */
  const columns = useMemo(() => [
    {
      field: 'groupLabel',
      headerName: GROUP_LABEL_HEADERS[groupBy] || 'Group',
      flex: 1.8,
      minWidth: 140,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
            {groupBy === 'league' ? leagueLabel(params.value) : params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'bets',
      headerName: 'Bets',
      flex: 0.8,
      minWidth: 80,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => <NumCell value={params.value} />,
    },
    {
      field: 'wins',
      headerName: 'Wins',
      flex: 0.8,
      minWidth: 80,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => <NumCell value={params.value} />,
    },
    {
      field: 'losses',
      headerName: 'Losses',
      flex: 0.8,
      minWidth: 80,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => <NumCell value={params.value} />,
    },
    {
      field: 'winRate',
      headerName: 'Win %',
      flex: 0.9,
      minWidth: 90,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => <PctCell value={params.value} />,
    },
    {
      field: 'projRoi',
      headerName: 'Proj ROI',
      flex: 0.9,
      minWidth: 100,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => <RoiCell value={params.value} />,
    },
    {
      field: 'actRoi',
      headerName: 'Act ROI',
      flex: 0.9,
      minWidth: 100,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => <RoiCell value={params.value} />,
    },
  ], [groupBy, leagueLabel]);

  /* ── Sort model — initialized based on groupBy, then user-controllable ── */
  const [sortModel, setSortModel] = useState([{ field: 'bets', sort: 'desc' }]);

  // Reset sort when groupBy changes (since some groupings have natural order)
  useEffect(() => {
    if (groupBy === 'edge' || groupBy === 'month') {
      setSortModel([]);
    } else {
      setSortModel([{ field: 'bets', sort: 'desc' }]);
    }
  }, [groupBy]);

  /* ── Summary bar values ── */
  const fmt = (val, mult = 100, dec = 1) =>
    val != null ? `${(val * mult).toFixed(dec)}%` : '-';
  const fmtSigned = (val) =>
    val != null ? `${val >= 0 ? '+' : ''}${(val * 100).toFixed(2)}%` : '-';

  /* ── Render ── */
  return (
    <ThemeProvider theme={customTheme}>
      <StyledEngineProvider injectFirst>
        <CssBaseline />
        <Box sx={{
          width: '100%',
          height: 'calc(100vh - 64px)',
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>

          {/* ── Filter bar ── */}
          <Box className="filter-section" sx={{ flexShrink: 0 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">

              {/* Group By — highlighted */}
              <FormControl variant="outlined" size="small" sx={{ minWidth: 130 }}>
                <InputLabel shrink sx={{ fontSize: '0.875rem' }}>Group By</InputLabel>
                <Select
                  label="Group By"
                  notched
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                  sx={{
                    fontSize: '0.875rem',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ff1744' },
                  }}
                >
                  {GROUP_BY_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Divider */}
              <Box sx={{ width: '1px', height: 28, backgroundColor: '#444', mx: 0.5 }} />

              {/* League */}
              <FormControl variant="outlined" size="small" sx={{ minWidth: 100 }}>
                <InputLabel shrink sx={{ fontSize: '0.875rem' }}>League</InputLabel>
                <Select
                  label="League"
                  notched
                  value={league}
                  onChange={(e) => setLeague(e.target.value)}
                  renderValue={(val) => val === 'All' ? 'All' : leagueLabel(val)}
                  sx={{ fontSize: '0.875rem' }}
                >
                  {['All', ...availableLeagues].map(lg => (
                    <MenuItem key={lg} value={lg}>{lg === 'All' ? 'All' : leagueLabel(lg)}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Book (multi-select, populated from data) */}
              <FormControl variant="outlined" size="small" sx={{ minWidth: 130 }}>
                <InputLabel shrink sx={{ fontSize: '0.875rem' }}>Book</InputLabel>
                <Select
                  multiple
                  label="Book"
                  notched
                  value={bookFilter}
                  onChange={handleBookChange}
                  renderValue={renderBookValue}
                  sx={{ fontSize: '0.875rem' }}
                >
                  <MenuItem value="all">
                    <Checkbox
                      checked={bookFilter.length === availableBooks.length && availableBooks.length > 0}
                      indeterminate={bookFilter.length > 0 && bookFilter.length < availableBooks.length}
                    />
                    <ListItemText primary="All" />
                  </MenuItem>
                  {availableBooks.map(book => (
                    <MenuItem key={book} value={book}>
                      <Checkbox checked={bookFilter.includes(book)} />
                      <ListItemText primary={book} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Min Edge */}
              <TextField
                value={minEdge}
                onChange={(e) => {
                  const val = e.target.value;
                  setMinEdge(val === '' ? '' : parseFloat(val));
                }}
                label="Min Edge %"
                size="small"
                type="number"
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: -50, max: 50, step: 0.5 }}
                sx={{
                  width: 100,
                  '& .MuiInputBase-input': { color: 'white' },
                }}
              />

              {/* From */}
              <TextField
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                label="From"
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
                sx={{
                  width: 145,
                  '& .MuiInputBase-input': { color: 'white' },
                }}
              />

              {/* To */}
              <TextField
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                label="To"
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
                sx={{
                  width: 145,
                  '& .MuiInputBase-input': { color: 'white' },
                }}
              />
            </Stack>
          </Box>

          {/* ── Summary bar ── */}
          {summary && (
            <Box sx={{
              backgroundColor: '#1a1a1a',
              px: 2,
              py: 1.25,
              display: 'flex',
              gap: 3,
              borderBottom: '1px solid #333',
              flexWrap: 'wrap',
              flexShrink: 0,
            }}>
              {[
                { label: 'Bets', value: summary.bets?.toLocaleString() },
                { label: 'Act ROI', value: fmtSigned(summary.actRoi), color: roiColor(summary.actRoi) },
                { label: 'Proj ROI', value: fmtSigned(summary.projRoi), color: roiColor(summary.projRoi) },
                { label: 'Win Rate', value: fmt(summary.winRate) },
                { label: 'Avg BEW', value: fmt(summary.avgBew) },
              ].map(({ label, value, color }) => (
                <Box key={label} sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                  <Typography sx={{ color: '#888', fontSize: '0.8125rem' }}>{label}:</Typography>
                  <Typography sx={{ color: color || '#fff', fontSize: '0.8125rem', fontWeight: 600 }}>
                    {value}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* ── Data Grid ── */}
          <Box className="table-section" sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <Box className="data-grid-container" sx={{ height: '100%', boxSizing: 'border-box' }}>
              {error ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography sx={{ color: '#f44336' }}>Error: {error}</Typography>
                </Box>
              ) : loading ? (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  gap: 2,
                }}>
                  <CircularProgress sx={{ color: '#ff1744' }} />
                  <Typography sx={{ color: '#bbb' }}>
                    Loading results...
                  </Typography>
                  <Typography sx={{ color: '#888', fontSize: '0.75rem' }}>
                    Analyzing large dataset — hang tight.
                  </Typography>
                </Box>
              ) : rows.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography sx={{ color: '#888' }}>
                    {summary ? 'No data matches your current filters' : 'Loading...'}
                  </Typography>
                </Box>
              ) : (
                <DataGridPro
                  rows={rows}
                  columns={columns}
                  loading={loading}
                  pageSize={50}
                  rowsPerPageOptions={[25, 50, 100]}
                  pagination
                  paginationMode="client"
                  sortModel={sortModel}
                  onSortModelChange={setSortModel}
                  sortingMode="client"
                  disableSelectionOnClick
                  disableColumnFilter
                  disableColumnMenu
                  getRowId={(row) => row.id}
                  hideFooterSelectedRowCount
                  sx={{
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: '#1e1e1e',
                      borderBottom: '2px solid #444',
                    },
                    '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
                    '& .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      fontSize: '0.875rem',
                      fontWeight: 700,
                      letterSpacing: '0.02rem',
                      color: '#ffffff !important',
                    },
                    '& .MuiDataGrid-cell': {
                      color: '#ffffff !important',
                      fontSize: '0.875rem',
                      borderRight: '1px solid rgba(255, 255, 255, 0.3)',
                    },
                    '& .MuiDataGrid-cell:last-child': { borderRight: 'none' },
                    '& .MuiDataGrid-cell:focus': { outline: 'none' },
                    '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
                    '& .MuiDataGrid-row:hover': { backgroundColor: '#1a1a1a' },
                    '& .MuiDataGrid-columnSeparator': {
                      visibility: 'visible',
                      color: 'rgba(255, 255, 255, 0.3)',
                    },
                    '& .MuiDataGrid-sortIcon': { color: '#ffffff !important' },
                    '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      color: '#ffffff !important',
                      fontSize: '0.875rem',
                    },
                    '& .MuiSelect-icon': { color: '#ffffff !important' },
                    '& .MuiDataGrid-footerContainer': {
                      backgroundColor: '#1e1e1e',
                      color: '#ffffff !important',
                    },
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </StyledEngineProvider>
    </ThemeProvider>
  );
}